import React, { useEffect, useState } from "react";
import { NavLink, Link, useNavigate, useLocation } from "react-router-dom";
import { Settings as SettingsIcon, Plus } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import NotificationBell from "./NotificationBell";
import ComposePostModal from "./ComposePostModal";

const links = [
  { to: "/traditions", label: "Traditions" },
  { to: "/oracle", label: "Oracle" },
  { to: "/weekly", label: "Weekly" },
  { to: "/messages", label: "Messages", showBadge: true },
  { to: "/settings#settings", label: "Settings" },
];

export default function Navigation() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [dmUnread, setDmUnread] = useState(0);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composePrefill, setComposePrefill] = useState(null);

  // Any page can open the compose modal by dispatching this custom event.
  // Optional `event.detail = { title, body }` seeds the fields.
  useEffect(() => {
    if (!user) return; // Composing requires an account.
    const onOpen = (e) => {
      setComposePrefill(e?.detail || null);
      setComposeOpen(true);
    };
    window.addEventListener("cor:open-compose", onOpen);
    return () => window.removeEventListener("cor:open-compose", onOpen);
  }, [user]);

  // Poll the DM unread count every 60s while signed in.
  useEffect(() => {
    if (!user) { setDmUnread(0); return; }
    let cancelled = false;
    const fetchUnread = async () => {
      try {
        const { data } = await api.get("/messages/unread-count");
        if (!cancelled) setDmUnread(data?.unread || 0);
      } catch { /* ignore */ }
    };
    fetchUnread();
    const t = setInterval(fetchUnread, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [user]);

  return (
    <nav
      data-testid="main-nav"
      className="sticky top-0 z-50 bg-background/95 md:bg-background/80 md:backdrop-blur-md hairline-b will-change-transform"
      style={{ transform: "translateZ(0)" }}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-5 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
          <Glyph />
          <div className="leading-none">
            <div className="serif text-[22px] tracking-tight">
              <span className="text-[hsl(var(--primary))]">Cor</span>
              <span className="text-[hsl(var(--accent))]">·</span>
              <span className="text-foreground">relate</span>
            </div>
            <div className="label mt-1 hidden sm:block">The same truth, in every tongue</div>
          </div>
        </Link>

        {/* DESKTOP NAV */}
        <div className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `relative mono text-[11px] uppercase tracking-[0.22em] link-underline ${
                  isActive ? "text-[hsl(var(--primary))]" : "text-muted-foreground"
                }`
              }
            >
              {l.label}
              {l.showBadge && user && dmUnread > 0 && (
                <span
                  data-testid={`nav-${l.label.toLowerCase()}-badge`}
                  className="absolute -top-1.5 -right-2.5 min-w-[16px] h-4 px-1 inline-flex items-center justify-center mono text-[9px] tabular-nums bg-[hsl(var(--primary))] text-background rounded-full"
                >
                  {dmUnread > 99 ? "99+" : dmUnread}
                </span>
              )}
            </NavLink>
          ))}
        </div>

        <div className="flex items-center gap-3">
          {user && (
            <button
              type="button"
              data-testid="nav-compose"
              onClick={() => setComposeOpen(true)}
              className="relative w-9 h-9 inline-flex items-center justify-center rounded-full border border-[hsl(var(--primary)/0.55)] text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary)/0.08)] hover:border-[hsl(var(--primary))] transition-colors"
              title="Write a new post"
              aria-label="Write a new post"
            >
              <Plus size={18} />
            </button>
          )}
          <NotificationBell />
          {user ? (
            <>
              <Link
                to="/settings#you"
                data-testid="nav-user"
                className="hidden sm:inline mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground hover:text-[hsl(var(--primary))] transition-colors link-underline"
                title="Your archive"
              >
                {user.name?.split(" ")[0] || user.email}
              </Link>
              <button
                data-testid="logout-btn"
                onClick={async () => { await logout(); navigate("/"); }}
                className="hidden md:inline-flex btn-ghost"
              >
                Sign out
              </button>
            </>
          ) : (
            // "Enter" CTA is only relevant on the landing page — once a visitor
            // is already wandering the archive, the bottom tabs / top nav serve
            // them, and a redundant Enter button just adds noise.
            location.pathname === "/" && (
              <Link to="/login" data-testid="login-link" className="hidden md:inline-flex btn-ghost">
                Enter
              </Link>
            )
          )}

          {/* SETTINGS COG (mobile only — replaces the hamburger menu).
              The bottom tab bar now covers primary navigation, so this is
              a direct shortcut to the Settings page. */}
          <Link
            to="/settings#settings"
            data-testid="mobile-settings-btn"
            aria-label="Open settings"
            className="md:hidden p-2 text-foreground hover:text-[hsl(var(--primary))] transition-colors"
          >
            <SettingsIcon size={22} strokeWidth={1.5} />
          </Link>
        </div>
      </div>
      <ComposePostModal open={composeOpen} onClose={() => { setComposeOpen(false); setComposePrefill(null); }} prefill={composePrefill} />
    </nav>
  );
}

function Glyph() {
  // Minimal mark — a serif italic "C" rendered with the SAME font and colour as the "Cor"
  // wordmark beside it. By using HTML rather than an image, the C inherits the exact
  // Cormorant Garamond webfont weight + tracking the app already loads.
  return (
    <span
      aria-label="Cor·relate"
      className="serif italic font-medium leading-none select-none transition-transform group-hover:rotate-[-4deg] flex items-center justify-center"
      style={{
        fontSize: "44px",
        color: "hsl(var(--primary))",
        width: "44px",
        height: "44px",
        background: "#FFFFFF",
        borderRadius: "8px",
        border: "1px solid hsl(var(--border))",
        paddingRight: "4px",  // optical centring — italic C leans right
      }}
    >
      C
    </span>
  );
}
